/**
 * Sorting the Database grid.
 *
 * Every column carries a sort arrow (Simon Abbott, 26 Aug 2026). Clicking it
 * cycles A-Z -> Z-A -> back to the calling order the grid ships with, so sorting
 * is always something you can undo without reloading the page.
 *
 * Sorted in the browser, not the API: the grid is unpaginated (the endpoint sets
 * pagination_class = None), so what is on screen is the whole list and sorting it
 * here cannot hide rows behind a page boundary.
 *
 * A column is the same {key, label, get} shape the grid already uses, so these
 * work for the built-in columns and for an agent's own custom ones alike.
 */

// Hot > Warm > Cold, not alphabetical - the whole point of the column is urgency.
export const TEMP_RANK = { hot: 3, warm: 2, cold: 1 };

const SORT_COLLATOR = new Intl.Collator("en-AU", { numeric: true, sensitivity: "base" });

// A leading house number, unit or range: "48 - 54 ", "5/43 ", "100 ".
// Greedy, so a range keeps both halves: "48 - 54", not "48" and a stray "- 54".
const LEADING_NUMBER = /^([\d\s/\-–]+)\s+(\S.*)$/;

// Sorting a street address by its house number across unrelated streets is
// noise - it interleaves half of Windsor with half of Yarraville. An agent
// sorting the calling list by Address wants the STREET grouped and the numbers
// running up it, the way they would door-knock, so the leading number moves to
// the end of the sort key and the natural collator does the rest.
//   "48 - 54 Stanger St, Yarraville" -> "Stanger St, Yarraville 48 - 54"
const addressKey = (text) => {
  const m = LEADING_NUMBER.exec(text);
  return m ? `${m[2]} ${m[1].trim()}` : text;
};

// The value a column sorts on. Numbers compare as numbers, dates as dates, and
// anything else as text with natural number handling so "10 Smith St" sits after
// "2 Smith St" rather than before it.
export const sortValue = (col, record) => {
  let raw;
  try {
    raw = col.get(record);
  } catch {
    raw = null; // a custom column with no data on this row
  }
  if (raw === null || raw === undefined || raw === "") return null;
  if (col.temp) return TEMP_RANK[String(raw).toLowerCase()] ?? 0;
  if (Array.isArray(raw)) raw = raw.join(", ");
  if (typeof raw === "number") return raw;
  const text = String(raw).trim();
  if (!text) return null;
  if (col.key === "address") return addressKey(text);
  // ISO date / datetime straight off the API sorts correctly as a string.
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text;
  // A pure number, with or without $ and thousands separators.
  const numeric = text.replace(/[$,\s]/g, "");
  if (numeric && !isNaN(Number(numeric))) return Number(numeric);
  return text;
};

// "Looking to sell" also carries Homer's For sale / Under offer / Withdrawn chip.
// Sorting the column has to group those rows (Christian Gravias, Monday
// 13198140343, 22 Oct 2026: "trying to click the filter to sort"); sorting on
// the agent's typed text alone left the chips scattered among blank rows.
export const MARKET_RANK = { for_sale: 1, under_offer: 2, withdrawn: 3 };

const marketOf = (record) => {
  const op = record?.owner_profile;
  const rank = MARKET_RANK[op?.market_status];
  return rank ? { rank, since: String(op.market_since || "") } : null;
};

// Tagged rows lead in both directions, the same way blanks always sink: A-Z runs
// For sale -> Under offer -> Withdrawn, Z-A the reverse, and inside each group the
// most recent listing or withdrawal comes first because that owner is the call to
// make. Rows with no tag fall through to the agent's own text.
const compareMarket = (a, b, sign) => {
  const ma = marketOf(a);
  const mb = marketOf(b);
  if (!ma && !mb) return null;
  if (!ma) return 1;
  if (!mb) return -1;
  if (ma.rank !== mb.rank) return (ma.rank - mb.rank) * sign;
  if (ma.since !== mb.since) return ma.since < mb.since ? 1 : -1;
  return null;
};

// Blank cells always sink to the bottom, in both directions: an agent sorting by
// Mobile wants the rows they can ring first, not a screen of empty ones.
export const compareBy = (col, dir) => (a, b) => {
  if (col.market) {
    const byMarket = compareMarket(a, b, dir === "desc" ? -1 : 1);
    if (byMarket !== null) return byMarket;
  }
  const va = sortValue(col, a);
  const vb = sortValue(col, b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = dir === "desc" ? -1 : 1;
  if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
  return SORT_COLLATOR.compare(String(va), String(vb)) * sign;
};
